import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { getCollaboratorProfileHref } from '../../vacations.definitions';
import { formatPaidRestLastPeriod } from '../../utils/vacationPaidRest';
import type { PaidRestRow } from '../../models/vacationPaidRest.types';

export interface VacationPaidRestTableProps {
    rows: PaidRestRow[];
    isLoading?: boolean;
    selectedRow?: PaidRestRow | null;
    onRowSelect?: (row: PaidRestRow) => void;
}

export function VacationPaidRestTable({
    rows,
    isLoading = false,
    selectedRow,
    onRowSelect,
}: VacationPaidRestTableProps) {
    const currentYear = new Date().getFullYear();

    if (isLoading) {
        return (
            <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg border border-raro-gray-100 p-3">
                        <div className="h-4 w-32 animate-pulse rounded bg-raro-gray-100" />
                        <div className="h-4 w-24 animate-pulse rounded bg-raro-gray-100" />
                        <div className="h-4 w-20 animate-pulse rounded bg-raro-gray-100" />
                    </div>
                ))}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="rounded-lg border border-raro-gray-200 bg-raro-gray-50 p-6 text-center">
                <p className="text-sm text-raro-gray-500 font-montserrat">
                    Nenhum fornecedor com dados de descanso remunerado.
                </p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-raro-gray-200">
            <table className="w-full text-sm">
                <thead className="border-b border-raro-gray-200 bg-raro-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left font-semibold text-raro-gray-700 font-montserrat">
                            Nome
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-raro-gray-700 font-montserrat">
                            CNPJ
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-raro-gray-700 font-montserrat">
                            Direito
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-raro-gray-700 font-montserrat">
                            Último Descanso
                        </th>
                        <th className="px-4 py-3 text-center font-semibold text-raro-gray-700 font-montserrat">
                            Ação
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-raro-gray-200">
                    {rows.map((row) => (
                        <tr key={row.collaboratorId} className="hover:bg-raro-gray-50">
                            <td className="px-4 py-3">
                                <Link
                                    to={getCollaboratorProfileHref(row.collaboratorId)}
                                    className="font-medium text-raro-gray-900 underline-offset-2 transition-colors hover:text-primary-700 hover:underline font-montserrat"
                                >
                                    {row.collaboratorName}
                                </Link>
                            </td>
                            <td className="px-4 py-3 text-raro-gray-600 font-montserrat">
                                {row.cnpj ? row.cnpj : '—'}
                            </td>
                            <td className="px-4 py-3">
                                <span
                                    className={clsx(
                                        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                                        row.hasPaidRest
                                            ? 'bg-emerald-50 text-emerald-700'
                                            : 'bg-raro-gray-100 text-raro-gray-600'
                                    )}
                                >
                                    {row.hasPaidRest ? 'Sim' : 'Não'}
                                </span>
                            </td>
                            <td className="px-4 py-3 text-raro-gray-600 font-montserrat">
                                {formatPaidRestLastPeriod(row.lastPeriod)}
                            </td>
                            <td className="px-4 py-3 text-center">
                                <button
                                    type="button"
                                    onClick={() => onRowSelect?.(row)}
                                    className="inline-flex items-center rounded-lg border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-100 transition-colors font-montserrat"
                                >
                                    Ver histórico
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
